import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import apiClient from "../../../api/apiClient";
import { toast } from "react-toastify";
import SEO from "../../../components/common/SEO";
import noHospital from '../../../assets/images/no-hospital.png';
import confirmDeletion from '../../utils/confirmDeletion';


export default function ListingHospitals() {
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: "id", direction: "desc" });

  const pageSize = 20;

  const fetchHospitals = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get("/hospitals/list");
      if (res.data && res.data.success) {
        setHospitals(Array.isArray(res.data.data) ? res.data.data : []);
      }
    } catch (err) {
      toast.error("Failed to load hospitals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHospitals(); }, []);

  const handleDelete = async (id) => {
    if (!id || !(await confirmDeletion())) return;
    try {
      await apiClient.delete(`/hospitals/delete/${id}`);
      toast.success("Hospital deleted");
      setHospitals((prev) => prev.filter((h) => h.id !== id));
    } catch (err) { toast.error("Delete failed"); }
  };

  const getNestedValue = (obj, path) => (
    path.split(".").reduce((acc, part) => acc && acc[part], obj)
  );

  const sortedHospitals = useMemo(() => {
    const search = searchTerm.toLowerCase();
    const filtered = hospitals.filter((h) => {
      const checks = [
        h?.hospital_name,
        h?.city,
        h?.consultation_fees && String(h.consultation_fees),
        h?.created_at,
      ];
      return checks.some((value) => String(value || "").toLowerCase().includes(search));
    });

    return filtered.sort((a, b) => {
      const aValue = getNestedValue(a, sortConfig.key) ?? "";
      const bValue = getNestedValue(b, sortConfig.key) ?? "";

      if (!Number.isNaN(Number(aValue)) && !Number.isNaN(Number(bValue))) {
        return sortConfig.direction === "asc"
          ? Number(aValue) - Number(bValue)
          : Number(bValue) - Number(aValue);
      }

      return sortConfig.direction === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [hospitals, searchTerm, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedHospitals.length / pageSize));
  const paginatedHospitals = sortedHospitals.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const renderSortArrow = (key) => {
    if (sortConfig.key !== key) return "";
    return sortConfig.direction === "asc" ? " ▲" : " ▼";
  };

  const renderStatusBadge = (status, reason) => {
    let badgeClass = 'bg-secondary';
    if (status === 'Published') badgeClass = 'bg-success';
    else if (status === 'Under Review') badgeClass = 'bg-warning';
    else if (status === 'Rejected') badgeClass = 'bg-danger';

    return (
      <div>
        <span className={`badge ${badgeClass}`}>{status || 'Published'}</span>
        {status === 'Rejected' && reason && (
          <div className="text-danger mt-1" style={{ fontSize: '11px', whiteSpace: 'normal', maxWidth: '150px' }}>
            <i className="ri-error-warning-line align-middle me-1"></i>
            {reason}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="row">
      <SEO title="Hospitals Management | Mangoloops" />
      <div className="col-lg-12">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h4 className="card-title mb-0">Hospitals Listing</h4>
            <div className="d-flex flex-wrap gap-2">
              <input
                type="text"
                className="form-control"
                placeholder="Search hospitals..."
                value={searchTerm}
                onChange={handleSearchChange}
                style={{ width: "250px" }}
              />
              <Link to="/admin/add-hospital" className="btn btn-soft-secondary">Add Hospital</Link>
            </div>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-bordered table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "80px" }}>Sr. No.</th>
                    <th style={{ width: "100px" }}>Image</th>
                    <th onClick={() => requestSort("hospital_name")} style={{ cursor: "pointer" }}>Hospital Name{renderSortArrow("hospital_name")}</th>
                    <th onClick={() => requestSort("city")} style={{ cursor: "pointer" }}>City{renderSortArrow("city")}</th>
                    <th onClick={() => requestSort("consultation_fees")} style={{ cursor: "pointer" }}>Consultation Fee{renderSortArrow("consultation_fees")}</th>
                    <th onClick={() => requestSort("status")} style={{ cursor: "pointer" }}>Status{renderSortArrow("status")}</th>
                    <th style={{ width: "100px" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="7" className="text-center py-4">Loading...</td></tr>
                  ) : paginatedHospitals.length > 0 ? paginatedHospitals.map((h, index) => (
                    <tr key={h.id}>
                      <td>{(currentPage - 1) * pageSize + index + 1}</td>
                      <td>
                        <img src={h.hospital_images?.[0]?.image_url || ""} alt="" className="rounded"
                          style={{ width: "60px", height: "60px", objectFit: "cover" }}
                          onError={(e) => e.target.src = noHospital} />
                      </td>
                      <td>
                        <Link to={`/admin/edit-hospital/${h.id}`} className="text-primary fw-medium">
                          {h.hospital_name}
                        </Link>
                      </td>
                      <td>{h.city || "Mumbai"}</td>
                      <td>₹{h.consultation_fees}</td>
                      <td>
                        {renderStatusBadge(h.status, h.rejection_reason)}
                      </td>
                      <td>
                        <button onClick={() => handleDelete(h.id)} className="btn btn-sm btn-outline-danger" title="Delete">
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="7" className="text-center py-4">No hospitals found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {sortedHospitals.length > 0 && (
              <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                <span className="text-muted">
                  Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, sortedHospitals.length)} of {sortedHospitals.length} results
                </span>
                <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}>Previous</button>
                  </li>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <li key={i} className={`page-item ${currentPage === i + 1 ? "active" : ""}`}>
                      <button className="page-link" onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                    </li>
                  ))}
                  <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}>Next</button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
